/**
 * Vendor Bot Shop - StoreFront Page
 * Product catalog with categories, search, filters (Customer view)
 * Matches Telegram Wallet MiniApp design pattern
 */

import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import {
  BottomNavigation,
  StoreHeader,
  ProductCard,
  CategoryTabs,
  SearchFilterBar
} from '../../components/customer';
import StoreUnavailable from './StoreUnavailable';
import { useTranslation } from '../../hooks/useTranslation';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  compare_at_price: number | null;
  product_type: 'physical' | 'downloadable' | 'keys' | 'subscription';
  image_urls: string[];
  stock_quantity: number;
  track_inventory: boolean;
  allow_backorder: boolean;
  is_featured: boolean;
  tags: string[];
}

interface Subcategory {
  id: number;
  name: string;
  product_count: number;
}

interface Category {
  id: number;
  name: string;
  description: string;
  product_count: number;
  subcategories?: Subcategory[];
}

interface StoreInfo {
  store_name: string;
  store_description: string;
  store_logo: string | null;
  store_logo_type: 'image' | 'emoji';
  store_logo_emoji: string;
  currency: string;
  currency_symbol: string;
  is_open: boolean;
}

interface Pagination {
  page: number;
  limit: number;
  totalPages: number;
  totalCount: number;
}

const StoreFront: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const { t } = useTranslation();

  // Store availability check
  const [storeAvailable, setStoreAvailable] = useState<boolean | null>(null);

  const [storeInfo, setStoreInfo] = useState<StoreInfo>({
    store_name: botUsername || 'Store',
    store_description: '',
    store_logo: null,
    store_logo_type: 'emoji',
    store_logo_emoji: '🛍️',
    currency: 'USD',
    currency_symbol: '$',
    is_open: true
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [viewMode, setViewMode] = useState<'products' | 'categories'>('products');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    totalPages: 1,
    totalCount: 0
  });

  const baseUrl = process.env.REACT_APP_API_URL || '';

  // Check store availability (subscription, suspension, etc.)
  useEffect(() => {
    const checkStoreStatus = async () => {
      try {
        const res = await fetch(`${baseUrl}/api/shop/${botUsername}/status`);
        const data = await res.json();
        if (data.success && data.data) {
          setStoreAvailable(data.data.is_available);
        } else {
          setStoreAvailable(true); // Default to available on error
        }
      } catch (error) {
        console.error('Store status check failed:', error);
        setStoreAvailable(true); // Default to available on error
      }
    };

    if (botUsername) {
      checkStoreStatus();
    }
  }, [baseUrl, botUsername]);

  // Load store info
  useEffect(() => {
    const loadStoreInfo = async () => {
      try {
        const res = await fetch(`${baseUrl}/api/shop/${botUsername}/store-info`);
        const data = await res.json();
        if (data.success) {
          setStoreInfo(data.data);
        }
      } catch (error) {
        console.error('Error loading store info:', error);
      }
    };

    loadStoreInfo();
  }, [botUsername, baseUrl]);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await fetch(`${baseUrl}/api/shop/${botUsername}/categories`);
        const data = await res.json();
        if (data.success) {
          setCategories(data.data);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategories();
  }, [botUsername, baseUrl]);

  // Load cart count
  useEffect(() => {
    const loadCartCount = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      try {
        const res = await fetch(`${baseUrl}/api/cart/${botUsername}/count`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await res.json();
        if (data.success && data.data) {
          setCartCount(data.data.count || 0);
        }
      } catch (error) {
        console.error('Error loading cart count:', error);
      }
    };

    loadCartCount();
  }, [botUsername, baseUrl]);

  // Load products
  const loadProducts = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });

      if (selectedCategory) {
        params.append('category', selectedCategory.toString());
      }

      if (selectedSubcategory) {
        params.append('subcategory', selectedSubcategory.toString());
      }

      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const res = await fetch(`${baseUrl}/api/shop/${botUsername}/products?${params}`);
      const data = await res.json();

      if (data.success) {
        setProducts(data.data);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoading(false);
    }
  }, [botUsername, baseUrl, selectedCategory, selectedSubcategory, searchQuery]);

  useEffect(() => {
    loadProducts(1);
  }, [loadProducts]);

  // Handle search
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Handle category change from tabs
  const handleCategoryChange = (categoryId: number | string | null) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory(null); // Reset subcategory when category changes
  };

  // Handle category change from dropdown
  const handleDropdownCategoryChange = (categoryId: number | string | null) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory(null);
  };

  // Handle subcategory change
  const handleSubcategoryChange = (subcategoryId: number | string | null) => {
    setSelectedSubcategory(subcategoryId);
  };

  // Initialize Telegram WebApp
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();

      // Apply theme colors
      if (tg.themeParams?.bg_color) {
        tg.setBackgroundColor(tg.themeParams.bg_color);
        tg.setHeaderColor(tg.themeParams.bg_color);
      }
    }
  }, []);

  // Show loading while checking store status
  if (storeAvailable === null) {
    return (
      <PageContainer>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      </PageContainer>
    );
  }

  // Show unavailable page if store is blocked
  if (storeAvailable === false) {
    return <StoreUnavailable />;
  }

  return (
    <PageContainer>
      {/* Header with Logo, Products/Categories Toggle, Cart */}
      <StoreHeader
        storeLogo={storeInfo.store_logo}
        storeLogoType={storeInfo.store_logo_type}
        storeLogoEmoji={storeInfo.store_logo_emoji}
        cartCount={cartCount}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Main Content Container */}
      <MainContainer>
        {/* Store Closed Banner */}
        {!storeInfo.is_open && (
          <StoreClosedBanner>
            {t('shop.storeClosed')}
          </StoreClosedBanner>
        )}

        {viewMode === 'products' ? (
          <>
            {/* Search & Category Filter Bar */}
            <SearchFilterBar
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={handleDropdownCategoryChange}
              onSearch={handleSearch}
              searchPlaceholder={t('common.searchProducts')}
            />

            {/* Category Pills */}
            <CategoryTabs
              categories={categories}
              selectedCategory={selectedCategory}
              selectedSubcategory={selectedSubcategory}
              onCategorySelect={handleCategoryChange}
              onSubcategorySelect={handleSubcategoryChange}
              showAllTab={true}
            />

            {/* Products Section */}
            <Content>
              {isLoading ? (
                <LoadingContainer>
                  <Spinner />
                  <LoadingText>{t('common.loadingProducts')}</LoadingText>
                </LoadingContainer>
              ) : products.length === 0 ? (
                <EmptyState>
                  <EmptyIcon>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4.86 8.86l-3 3.87L9 13.14 6 17h12l-3.86-5.14z"/>
                    </svg>
                  </EmptyIcon>
                  <EmptyText>
                    {searchQuery
                      ? t('shop.noProductsFor', { query: searchQuery })
                      : t('shop.noProducts')}
                  </EmptyText>
                </EmptyState>
              ) : (
                <>
                  <ProductCount>
                    {pagination.totalCount} {t('common.products').toLowerCase()}
                  </ProductCount>

                  <ProductGrid>
                    {products.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        currency={storeInfo.currency}
                        currencySymbol={storeInfo.currency_symbol}
                      />
                    ))}
                  </ProductGrid>

                  {pagination.totalPages > 1 && (
                    <LoadMoreContainer>
                      {pagination.page < pagination.totalPages && (
                        <LoadMoreButton onClick={() => loadProducts(pagination.page + 1)}>
                          {t('common.loadMore')}
                        </LoadMoreButton>
                      )}
                      <PageInfo>
                        {t('common.page')} {pagination.page} {t('common.of')} {pagination.totalPages}
                      </PageInfo>
                    </LoadMoreContainer>
                  )}
                </>
              )}
            </Content>
          </>
        ) : (
          /* Categories View - List Style */
          <Content>
            <CategoriesList>
              {categories.length === 0 ? (
                <EmptyState>
                  <EmptyIcon>📁</EmptyIcon>
                  <EmptyText>{t('shop.noCategories')}</EmptyText>
                </EmptyState>
              ) : (
                categories.map((category) => (
                  <CategoryListItem
                    key={category.id}
                    onClick={() => {
                      handleCategoryChange(category.id);
                      setViewMode('products');
                    }}
                  >
                    <CategoryHeader>
                      <CategoryName>{category.name}</CategoryName>
                      <ProductCountBadge>
                        {category.product_count} {t('common.products')}
                      </ProductCountBadge>
                    </CategoryHeader>
                    {category.subcategories && category.subcategories.length > 0 && (
                      <SubcategoryTags>
                        {category.subcategories.map((sub) => (
                          <SubcategoryTag key={sub.id}>
                            {sub.name} ({sub.product_count})
                          </SubcategoryTag>
                        ))}
                      </SubcategoryTags>
                    )}
                  </CategoryListItem>
                ))
              )}
            </CategoriesList>
          </Content>
        )}
      </MainContainer>

      <BottomNavigation />
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--tg-theme-bg-color, #1a1a1a);
  padding-bottom: calc(70px + env(safe-area-inset-bottom, 0px));

  @media (min-width: 768px) {
    padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  }
`;

const MainContainer = styled.div`
  background: var(--tg-theme-bg-color, #1a1a1a);
  max-width: 768px;
  margin: 0 auto;

  @media (min-width: 768px) {
    max-width: 1024px;
  }
`;

const Content = styled.main`
  padding: 0 12px 16px;

  @media (min-width: 375px) {
    padding: 0 16px 16px;
  }

  @media (min-width: 768px) {
    padding: 0 24px 24px;
  }
`;

const StoreClosedBanner = styled.div`
  background: rgba(255, 160, 0, 0.15);
  color: #FFA000;
  padding: 10px 12px;
  margin: 0 12px 12px;
  border-radius: 10px;
  text-align: center;
  font-size: 13px;
  font-weight: 500;

  @media (min-width: 375px) {
    padding: 12px 16px;
    margin: 0 16px 16px;
    border-radius: 12px;
    font-size: 14px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--tg-theme-secondary-bg-color, #2c2c2e);
  border-top-color: var(--tg-theme-link-color, #00ff88);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: var(--tg-theme-hint-color, #8e8e93);
  font-size: 14px;
  margin-top: 12px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  color: var(--tg-theme-hint-color, #48484a);
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  color: var(--tg-theme-hint-color, #8e8e93);
  font-size: 15px;
  margin: 0;
`;

const ProductCount = styled.p`
  font-size: 13px;
  color: var(--tg-theme-hint-color, #8e8e93);
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  @media (max-width: 320px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  @media (min-width: 375px) {
    gap: 12px;
  }

  @media (min-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const LoadMoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
`;

const LoadMoreButton = styled.button`
  background: rgba(var(--tg-theme-link-color-rgb, 0, 255, 136), 0.15);
  color: var(--tg-theme-link-color, #00ff88);
  border: none;
  padding: 10px 24px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--tg-theme-link-color, #00ff88);
    color: var(--tg-theme-bg-color, #1a1a1a);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (min-width: 375px) {
    padding: 12px 32px;
    border-radius: 12px;
    font-size: 14px;
  }
`;

const PageInfo = styled.span`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #8e8e93);
`;

const CategoriesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 16px;
`;

const CategoryListItem = styled.button`
  width: 100%;
  background: var(--tg-theme-secondary-bg-color, #2c2c2e);
  border: none;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    background: rgba(var(--tg-theme-link-color-rgb, 0, 255, 136), 0.15);
  }

  &:active {
    transform: scale(0.99);
  }

  @media (min-width: 375px) {
    border-radius: 12px;
    padding: 16px;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const CategoryName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #ffffff);

  @media (min-width: 375px) {
    font-size: 18px;
  }
`;

const ProductCountBadge = styled.span`
  padding: 4px 8px;
  background: rgba(var(--tg-theme-link-color-rgb, 0, 255, 136), 0.15);
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  color: var(--tg-theme-link-color, #00ff88);

  @media (min-width: 375px) {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 14px;
  }
`;

const SubcategoryTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const SubcategoryTag = styled.span`
  display: inline-block;
  padding: 4px 8px;
  background: var(--tg-theme-bg-color, #1a1a1a);
  border-radius: 6px;
  font-size: 11px;
  color: var(--tg-theme-hint-color, #8e8e93);
`;

export default StoreFront;
